import {Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import {scale, timeStringToDayJs} from "../../utils";
import {TableComponents, TableVirtuoso} from "react-virtuoso";
import React, {useCallback, useEffect, useState} from "react";
import theme, {FONTS} from "../../constants/theme";
import {GOCCIA} from "../../constants/svgs";
import CustomSvg from "../custom-svg";
import Text from "../text";
import {DonatoreResponse, DonorTypeFilter} from "../../types";
import { DeleteForever } from "@mui/icons-material";
import Papa from 'papaparse';

interface Props {
  textSearch: string;
  filter: DonorTypeFilter;
  listaDonatori: DonatoreResponse[]
  onDelete: (prenotazioneId: number, raccoltaFasciaTurnoPrenotazioneId: number) => void;
  onExportCSV: (getCSVData: () => Blob) => void;
}

interface ColumnData {
  dataKey: keyof DonatoreResponse;
  label: string;
  numeric?: boolean;
  width?: string;
  height?: string;
  fontWeight?: number;
  donorType: 'sangue' | 'plasma';
  render?: (rowData: DonatoreResponse) => string;
}

const type = ['sangue', 'plasma'] as const
type myType = typeof type[number]
const someInstance:myType = type[type.length * Math.random() | 0]

const columns: ColumnData[] = [
  {
    width: scale(30),
    height: scale(22),
    fontWeight: 500,
    label: '',
    dataKey: 'raccoltaFasciaTurnoPrenotazioneId',
    donorType: someInstance
  },
  {
    width: scale(60),
    height: scale(22),
    fontWeight: 500,
    label: 'Ore',
    dataKey: 'hour',
    donorType: someInstance
  },
  {
    width: scale(250),
    height: scale(22),
    fontWeight: 700,
    label: 'Donatore',
    dataKey: 'firstName',  // metti un nome fittizio o lascialo vuoto
    donorType: someInstance,
    render: (rowData: DonatoreResponse) => `${rowData.firstName} ${rowData.lastName}`,
  },
  {
    width: scale(200),
    height: scale(22),
    fontWeight: 500,
    label: 'Codice fiscale',
    dataKey: 'CF',
    numeric: true,
    donorType: someInstance
  },
  {
    width: scale(150),
    height: scale(22),
    fontWeight: 500,
    label: 'telefono',
    dataKey: 'phone',
    donorType: someInstance
  },
  {
    width: scale(30),
    height: scale(22),
    fontWeight: 500,
    label: '',
    dataKey: 'delete' as keyof DonatoreResponse,
    donorType: someInstance
  },
];

const VirtuosoTableComponents: TableComponents<DonatoreResponse> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none' }} {...props} ref={ref} elevation={0} square  />
  )),
  Table: (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead
      {...props}
      ref={ref}
      style={{
        color: theme.palette.ceramic.contrastText
      }}
      sx={{color: theme.palette.ceramic.contrastText}} />
  )),
  TableRow,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function fixedHeaderContent() {
  return (
    <TableRow>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: 'background.paper',
            height: column.height,
            width: column.width,
            textAlign: 'center',
            padding: 0,
            paddingTop: scale(4),
            paddingBottom: scale(4),
            borderBottom: 'none',
            marginBottom: scale(40),
          }}
        >
          <Text
            text={column.label.toString()}
            color={theme.palette.text.secondary}
            fontFamily={FONTS.MONTSERRAT}
            fontWeight={700}
            fontSize={scale(18)}
            sx={{ display: 'block', textTransform: 'uppercase'}}
          />
        </TableCell>
      ))}
    </TableRow>
  );
}

const DonorList = ({textSearch, filter, listaDonatori, onDelete, onExportCSV}: Props) => {
  const [filteredData, setFilteredData] = useState<DonatoreResponse[]>(listaDonatori);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const filterData = useCallback(() => {
    const sortedData = [...listaDonatori].sort((a, b) => {
      return timeStringToDayJs(a.hour).diff(timeStringToDayJs(b.hour));
    });

    const filtered = sortedData.filter(el =>
      (filter === 'plasma & sangue' || el.type === filter) &&
      (el.firstName + ' ' + el.lastName).toLowerCase().includes(textSearch.toLowerCase()),
    );

    setFilteredData(filtered);
  }, [filter, listaDonatori, textSearch]);

  useEffect(() => {
    filterData()
  }, [filterData]);

  const getCSVBlob = useCallback((): Blob => {
    const csvData = filteredData.map(row => {
      const rowData: Record<string, any> = {};
      columns.forEach((column, index) => {
        let cellValue: string;
        if (index === 0) {
          cellValue = row.type === 'plasma' ? 'PL' : 'S';
          rowData[column.label || 'Tipo'] = cellValue;
          return;
        }
        if (column.render) {
          cellValue = column.render(row);
        } else {
          const val = row[column.dataKey];
          cellValue = val !== undefined && val !== null ? val.toString() : '';
        }

        if (index === columns.length - 2) {
          rowData[column.label] = `="${cellValue}"`;
        } else {
          rowData[column.label] = cellValue;
        }
      });
      return rowData;
    });

    const csv = Papa.unparse(csvData, { delimiter: ';' });
    return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  }, [filteredData]);

  useEffect(() => {
    if (onExportCSV) onExportCSV(getCSVBlob);
  }, [getCSVBlob, onExportCSV]);


  const rowContent = (index: number, row: DonatoreResponse) => {
    const isHovered = hoveredRowIndex === index;
    const isSelected = selectedRowIndex === index;

    const handleRowClick = () => {
      setSelectedRowIndex(index);
    };

    return columns.map((column, colIndex) => {
      if (isSelected && (column.dataKey === 'delete' as keyof DonatoreResponse)) {
        return (
          <TableCell
            key={colIndex}
            sx={{
              minWidth: scale(30),
              height: scale(29),
              minHeight: scale(29),
              maxHeight: scale(29),
              width: scale(30),
              borderRadius: '50%',
              backgroundColor: theme.palette.error.main,
              padding: 0,
              marginTop: scale(3),
              marginLeft: scale(2),
              // Flexbox per centrare
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              zIndex: 2
            }}
            onClick={() => {
              onDelete(row.prenotazioneId, row.raccoltaFasciaTurnoPrenotazioneId)
            }}
          >
            <DeleteForever
              sx={{
                color: theme.palette.white.main,
                height: scale(18),
              }}
            />
          </TableCell>
        )
      }
      return (
        <TableCell
          key={column.dataKey}
          align={column.numeric ? 'right' : 'left'}
          onClick={handleRowClick}
          onMouseEnter={() => setHoveredRowIndex(index)}
          onMouseLeave={() => setHoveredRowIndex(null)}
          sx={{
            cursor: 'pointer',
            textAlign: 'center',
            backgroundColor: isSelected ? theme.palette.primary.main : (isHovered ? '#e0e0e0' : 'transparent'),
            height: column.height,
            width: column.width,
            minWidth: column.width,
            maxWidth: column.width,
            padding: 0,
            paddingTop: scale(4),
            paddingBottom: scale(4),
            borderBottom: 'none',
            borderRadius: 0,
            boxShadow: 'none',
          }}
        >
          {colIndex === 0 ? (
            <CustomSvg
              svg={GOCCIA}
              sx={{ zIndex: 2, height: scale(18) }}
              color={row.type === 'sangue' ? 'blood' : 'plasma'}
            />
          ) : (
            <Text
              text={column.render
                ? column.render(row)
                : (row[column.dataKey] ?? '').toString()}
              color={isSelected ? theme.palette.text.primary : theme.palette.ceramic.contrastText}
              fontFamily={FONTS.MONTSERRAT}
              fontWeight={column.fontWeight}
              fontSize={scale(18)}
              sx={{ display: 'block', textTransform: 'uppercase' }}
            />
          )}
        </TableCell>
    )});
  };

  return (
    <Paper
      elevation={0}
      square
      sx={{
        boxShadow: 'none',
        border: 'none',
        width: scale(780 + 32)
      }}
      style={{ width: scale(745 + 32), height: scale(22 * 17 + 13 + 8 * 15), display: 'flex', flexDirection: 'column' }}>
      <TableVirtuoso
        style={{ flex: 1, borderTopLeftRadius: 0, borderTopRightRadius: 0, boxShadow: 'none' }}
        data={filteredData}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
      />
    </Paper>
  );
};


export default DonorList
